import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, BookOpen, CreditCard, 
  Mail, Settings, Bell, ChevronRight, Check, X, Download,
  Eye, MessageSquare, TrendingUp, Calendar, Video, Upload,
  Filter, Search, MoreVertical, Clock, AlertTriangle, RefreshCw,
  ExternalLink, Play, ChevronDown, Send, CalendarCheck, CalendarX,
  CalendarClock, Link2, MapPin, BarChart3, Edit3, Menu, Plus, CheckCircle,
  PenTool, ClipboardCheck, Sparkles, Loader2, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import ExamBuilder from './ExamBuilder';
import AssignmentReviewModal from './AssignmentReviewModal';
import AdminAuditLog from './admin/AdminAuditLog';
import { 
  OverviewSection, 
  AppointmentsSection, 
  MenteesSection,
  PaymentsSection,
  ReviewInboxSection,
  ContentSection,
  AssignmentsSection,
  AnnouncementsSection,
  RecordingsSection,
  CommunicationsSection,
  SettingsSection
} from './admin/sections';

/**
 * UNIFIED CONTROL CENTER - Fully Functional Version
 * Combines Admin Panel + Mentor Portal into one dashboard
 */

const UnifiedControlCenter = ({ user }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Real data states
  const [analytics, setAnalytics] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [mentees, setMentees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [atRiskMentees, setAtRiskMentees] = useState([]);
  const [appointments, setAppointments] = useState([]);
  
  // Filter states
  const [menteeFilter, setMenteeFilter] = useState('all');
  const [uploadFilter, setUploadFilter] = useState('all');
  const [appointmentFilter, setAppointmentFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selection states for bulk actions
  const [selectedUploads, setSelectedUploads] = useState([]);
  
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Suggest dates modal state
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [suggestedDates, setSuggestedDates] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  
  // Points management modal state
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [selectedMenteeForPoints, setSelectedMenteeForPoints] = useState(null);
  const [pointsToAward, setPointsToAward] = useState(10);
  const [pointsReason, setPointsReason] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  
  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ title: '', description: '', deadline: '', category: 'homework' });
  const [assignmentFile, setAssignmentFile] = useState(null);
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const assignmentFileInputRef = React.useRef(null);
  
  // Assignment Submissions state
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ 
    title: '', 
    message: '', 
    target_type: 'all', 
    target_user_ids: [],
    expires_at: '' 
  });
  
  // Bulk Email state
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmail, setBulkEmail] = useState({ subject: '', message: '', target_type: 'all' });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Exam Diagnostic state
  const [examDiagnostic, setExamDiagnostic] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [showExamImportModal, setShowExamImportModal] = useState(false);
  const [selectedCourseForImport, setSelectedCourseForImport] = useState(null);
  const [importingExams, setImportingExams] = useState(false);
  const [showExamBuilder, setShowExamBuilder] = useState(false);
  const [selectedLessonForExam, setSelectedLessonForExam] = useState(null);
  const [selectedCourseIdForExam, setSelectedCourseIdForExam] = useState(null);
  const [editingExam, setEditingExam] = useState(null);
  const [loadingExamForEdit, setLoadingExamForEdit] = useState(false);

  // Auto-generate exam (Whisper + Claude Sonnet 4.5)
  const [generatingLessonId, setGeneratingLessonId] = useState(null);
  const [generatingCourseId, setGeneratingCourseId] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  // Poll job status while a generation job is in flight.
  useEffect(() => {
    if (!activeJobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/auto-generate-exam/jobs/${activeJobId}`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setActiveJob(data);
        if (data.status === 'completed' || data.status === 'cancelled') {
          setGeneratingLessonId(null);
          setGeneratingCourseId(null);
          const ok = data.succeeded || 0;
          const fail = data.failed || 0;
          if (fail === 0) toast.success(`Generated ${ok} exam${ok === 1 ? '' : 's'} ✨`);
          else toast.warning(`Generated ${ok}; ${fail} failed. Click row for details.`);
          fetchExamDiagnostic();
        }
      } catch (e) {
        console.error('Job poll failed:', e);
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeJobId]);

  const startAutoGenerate = async ({ lessonId, courseId }) => {
    try {
      const url = lessonId
        ? `${API_URL}/api/admin/auto-generate-exam/lesson/${lessonId}`
        : `${API_URL}/api/admin/auto-generate-exam/bulk`;
      const body = lessonId
        ? { num_questions: 12, passing_score: 100, overwrite: true }
        : { course_id: courseId, num_questions: 12, passing_score: 100, overwrite: true };
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.job_id) {
        toast.error(data.detail || 'Failed to start generation');
        return;
      }
      if (lessonId) setGeneratingLessonId(lessonId);
      if (courseId) setGeneratingCourseId(courseId);
      setActiveJobId(data.job_id);
      setActiveJob(null);
      toast.info(`Generation started — ${data.total_lessons} lesson${data.total_lessons === 1 ? '' : 's'} queued`);
    } catch (e) {
      console.error('Start generation failed:', e);
      toast.error('Failed to start generation');
    }
  };

  const cancelActiveJob = async () => {
    if (!activeJobId) return;
    try {
      await fetch(`${API_URL}/api/admin/auto-generate-exam/jobs/${activeJobId}/cancel`, {
        method: 'POST',
        headers,
      });
    } catch (e) { console.error(e); }
  };
  
  // Session Recordings state
  const [showAddRecordingModal, setShowAddRecordingModal] = useState(false);
  const [liveSessions, setLiveSessions] = useState([]);
  const [existingRecordings, setExistingRecordings] = useState([]);
  const [newRecording, setNewRecording] = useState({
    session_id: '',
    recording_url: '',
    title: '',
    description: '',
    duration_minutes: 60,
    is_public: false
  });
  const [addingRecording, setAddingRecording] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAnalytics(),
        fetchUploads(),
        fetchMentees(),
        fetchCourses(),
        fetchEbooks(),
        fetchPayments(),
        fetchAppointments(),
        fetchAssignments(),
        fetchAnnouncements(),
        fetchLiveSessions(),
        fetchExistingRecordings(),
        fetchAssignmentSubmissions()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mentor/analytics`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (e) { console.error('Analytics error:', e); }
  };

  const fetchUploads = async () => {
    try {
      const res = await fetch(`${API_URL}/api/uploads/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUploads(data);
      }
    } catch (e) { console.error('Uploads error:', e); }
  };

  const fetchMentees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mentor/mentees`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMentees(data);
        
        // Filter at-risk mentees - ONLY enrolled/paid mentees who are inactive 14+ days
        // Enrolled mentees have an enrolled_mentorship_track OR membership_tier === 'mentorship'
        const enrolledMentees = data.filter(m => 
          m.enrolled_mentorship_track || 
          m.membership_tier === 'mentorship' ||
          m.has_paid
        );
        
        const atRisk = enrolledMentees.filter(m => {
          if (!m.last_active) return true; // Enrolled but never active
          const lastActive = new Date(m.last_active);
          const daysSince = (Date.now() - lastActive) / (1000 * 60 * 60 * 24);
          return daysSince > 14;
        });
        setAtRiskMentees(atRisk);
      }
    } catch (e) { console.error('Mentees error:', e); }
  };

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/courses`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCourses(data);
      }
    } catch (e) { console.error('Courses error:', e); }
  };

  const fetchEbooks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/library/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEbooks(data.filter(item => item.type === 'ebook'));
      }
    } catch (e) { console.error('Ebooks error:', e); }
  };

  const fetchAssignmentSubmissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assignments/submissions/all`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAssignmentSubmissions(data.submissions || []);
      }
    } catch (e) { console.error('Assignment submissions error:', e); }
  };

  // Sync library from Google Drive (fixes title/cover mismatches)
  const [syncingLibrary, setSyncingLibrary] = useState(false);
  
  const handleSyncLibrary = async () => {
    if (!window.confirm('This will re-sync all ebook titles and covers from Google Drive. This may take a few minutes. Continue?')) {
      return;
    }
    
    setSyncingLibrary(true);
    try {
      const res = await fetch(`${API_URL}/api/library/admin/sync-from-drive`, {
        method: 'POST',
        headers
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Library synced! ${data.success_count} ebooks updated.`);
        fetchEbooks();  // Refresh ebook list
      } else {
        toast.error(data.detail || 'Sync failed');
      }
    } catch (e) {
      toast.error('Error syncing library: ' + e.message);
    } finally {
      setSyncingLibrary(false);
    }
  };

  // ============== AI CATEGORIZATION ==============
  // Classifies every ebook in the library against Pastor Obed's 7x49 taxonomy
  // using Claude (Emergent LLM Key). Idempotent, safe to re-run.
  const [categorizeLoading, setCategorizeLoading] = useState(false);
  const [categorizeResult, setCategorizeResult] = useState(null);

  const handleCategorizeLibrary = async () => {
    if (!window.confirm('Run AI categorization on every ebook? Costs a few cents in LLM calls and takes ~30-60 seconds.')) return;
    setCategorizeLoading(true);
    setCategorizeResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin-tools/library/categorize-all`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        setCategorizeResult({ ok: true, ...data });
        toast.success(`Classified ${data.classified} ebooks across ${Object.keys(data.category_counts || {}).length} categories.`);
        fetchEbooks();
      } else {
        setCategorizeResult({ ok: false, error: data.detail || 'Categorization failed', status: res.status });
        toast.error(data.detail || `Failed (HTTP ${res.status})`);
      }
    } catch (e) {
      setCategorizeResult({ ok: false, error: e.message });
      toast.error('Error: ' + e.message);
    } finally {
      setCategorizeLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/override/payments`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
      }
    } catch (e) { console.error('Payments error:', e); }
  };

  const fetchAppointments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAppointments(data);
      }
    } catch (e) { console.error('Appointments error:', e); }
  };

  const fetchAssignments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assignments/global/all`, { headers });
      if (res.ok) {
        const data = await res.json();
        // Ensure we have an array with valid assignments
        const assignmentsList = data.assignments || data || [];
        setAssignments(Array.isArray(assignmentsList) ? assignmentsList.filter(a => a && a.id) : []);
      }
    } catch (e) { 
      console.error('Assignments error:', e); 
      setAssignments([]);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/admin/list`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.notifications || []);
      }
    } catch (e) { console.error('Announcements error:', e); }
  };

  // ============== ACTION HANDLERS ==============

  // Approve upload
  const handleApproveUpload = async (uploadId) => {
    const feedback = prompt('Enter feedback for the mentee (optional):') || 'Great work! Your submission has been approved.';
    try {
      const res = await fetch(`${API_URL}/api/uploads/${uploadId}/review`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: 'approved', feedback })
      });
      if (res.ok) {
        toast.success('Upload approved! Mentee has been notified.');
        fetchUploads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to approve upload');
      }
    } catch (e) {
      toast.error('Error approving upload');
    }
  };

  // Reject upload
  const handleRejectUpload = async (uploadId) => {
    const feedback = prompt('Enter feedback/reason for the mentee:') || 'Please review the requirements and resubmit.';
    try {
      const res = await fetch(`${API_URL}/api/uploads/${uploadId}/review`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: 'rejected', feedback })
      });
      if (res.ok) {
        toast.success('Upload marked for revision. Mentee has been notified.');
        fetchUploads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to reject upload');
      }
    } catch (e) {
      toast.error('Error rejecting upload');
    }
  };

  // Download file
  const handleDownloadFile = async (fileUrl, fileName, fileExists = true) => {
    if (!fileExists) {
      toast.error('File unavailable - The file no longer exists on the server. This may happen after server restarts.');
      return;
    }
    try {
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_URL}${fileUrl}`;
      const res = await fetch(fullUrl, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('File downloaded');
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to download file');
      }
    } catch (e) {
      toast.error('Failed to download file - Network error');
    }
  };

  // Bulk approve selected uploads
  const handleBulkApprove = async () => {
    if (selectedUploads.length === 0) {
      toast.error('No uploads selected');
      return;
    }
    for (const id of selectedUploads) {
      await handleApproveUpload(id);
    }
    setSelectedUploads([]);
  };

  // Send message to mentee
  const handleMessageMentee = async (menteeId, menteeName) => {
    const message = prompt(`Send message to ${menteeName}:`);
    if (!message) return;
    
    try {
      const res = await fetch(`${API_URL}/api/mentor/quick-actions/message-mentee`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mentee_id: menteeId, message })
      });
      if (res.ok) {
        toast.success('Message sent!');
      } else {
        toast.error('Failed to send message');
      }
    } catch (e) {
      toast.error('Error sending message');
    }
  };

  // Navigate to mentee profile
  const handleViewMenteeProfile = (menteeId) => {
    navigate(`/control-center/mentee/${menteeId}`);
  };

  // Navigate to content management
  const handleEditCourse = (courseId) => {
    navigate(`/admin/content?tab=courses&edit=${courseId}`);
  };

  // ============== ASSIGNMENT HANDLERS ==============
  
  const handleCreateAssignment = async () => {
    if (!newAssignment.title || !newAssignment.deadline) {
      toast.error('Please fill in title and deadline');
      return;
    }
    
    setCreatingAssignment(true);
    
    try {
      // Ensure deadline is in ISO format
      let deadlineISO = newAssignment.deadline;
      if (!deadlineISO.includes('T')) {
        deadlineISO = `${newAssignment.deadline}T23:59:00Z`;
      }
      
      // Use FormData for file upload support
      const formData = new FormData();
      formData.append('title', newAssignment.title);
      formData.append('description', newAssignment.description || '');
      formData.append('deadline', deadlineISO);
      formData.append('category', newAssignment.category || 'homework');
      
      // Add file if selected
      if (assignmentFile) {
        formData.append('file', assignmentFile);
      }
      
      // Use the new endpoint with file support
      const res = await fetch(`${API_URL}/api/assignments/global/with-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });
      
      const data = await res.json().catch(() => ({}));
      
      if (res.ok) {
        const notifiedCount = data.notified_count || 0;
        const fileNote = data.has_attachment ? ' (with file)' : '';
        toast.success(`Assignment created${fileNote}! ${notifiedCount} enrolled mentee(s) notified.`);
        setShowAssignmentModal(false);
        setNewAssignment({ title: '', description: '', deadline: '', category: 'homework' });
        setAssignmentFile(null);
        if (assignmentFileInputRef.current) {
          assignmentFileInputRef.current.value = '';
        }
        fetchAssignments();
      } else {
        toast.error(data.detail || 'Failed to create assignment');
      }
    } catch (e) {
      console.error('Assignment creation error:', e);
      toast.error('Error creating assignment: ' + (e.message || 'Unknown error'));
    } finally {
      setCreatingAssignment(false);
    }
  };
  
  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assignments/global/${assignmentId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Assignment deleted');
        fetchAssignments();
      }
    } catch (e) {
      toast.error('Error deleting assignment');
    }
  };

  // Take an assignment down without deleting it — hides from mentees but
  // keeps the record (and any submissions) for admin review. Reversible.
  const handleTakedownAssignment = async (assignmentId) => {
    if (!window.confirm('Take this assignment down? Mentees will no longer see it. You can restore it anytime.')) return;
    try {
      const res = await fetch(`${API_URL}/api/assignments/global/${assignmentId}/takedown`, {
        method: 'PATCH',
        headers
      });
      if (res.ok) {
        toast.success('Assignment taken down — no longer visible to mentees');
        fetchAssignments();
      } else {
        toast.error('Failed to take down assignment');
      }
    } catch (e) {
      toast.error('Error taking down assignment');
    }
  };

  const handleRestoreAssignment = async (assignmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/assignments/global/${assignmentId}/restore`, {
        method: 'PATCH',
        headers
      });
      if (res.ok) {
        toast.success('Assignment restored — visible to mentees again');
        fetchAssignments();
      } else {
        toast.error('Failed to restore assignment');
      }
    } catch (e) {
      toast.error('Error restoring assignment');
    }
  };

  // ============== ANNOUNCEMENT HANDLERS ==============
  
  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Please fill in title and message');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/admin/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newAnnouncement)
      });
      if (res.ok) {
        toast.success('Announcement created! All mentees will see it.');
        setShowAnnouncementModal(false);
        setNewAnnouncement({ title: '', message: '', target_type: 'all', target_user_ids: [], expires_at: '' });
        fetchAnnouncements();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create announcement');
      }
    } catch (e) {
      toast.error('Error creating announcement');
    }
  };
  
  const handleDeleteAnnouncement = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/admin/${notificationId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      }
    } catch (e) {
      toast.error('Error deleting announcement');
    }
  };

  // ============== BULK EMAIL HANDLERS ==============
  
  const handleSendBulkEmail = async () => {
    if (!bulkEmail.subject || !bulkEmail.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/admin/bulk-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(bulkEmail)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sending emails to ${data.target_count} mentees!`);
        setShowBulkEmailModal(false);
        setBulkEmail({ subject: '', message: '', target_type: 'all' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send bulk email');
      }
    } catch (e) {
      toast.error('Error sending bulk email');
    } finally {
      setSendingEmail(false);
    }
  };

  // ============== PAYMENT HANDLERS ==============

  // Fetch live sessions for recording upload
  const fetchLiveSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/session-recordings/admin/sessions`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLiveSessions(data.sessions || []);
      }
    } catch (e) {
      console.error('Error fetching live sessions:', e);
    }
  };
  
  // Fetch existing recordings
  const fetchExistingRecordings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/session-recordings/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExistingRecordings(data.recordings || []);
      }
    } catch (e) {
      console.error('Error fetching recordings:', e);
    }
  };

  // Fetch exam diagnostic
  const fetchExamDiagnostic = async () => {
    setLoadingDiagnostic(true);
    try {
      const res = await fetch(`${API_URL}/api/exams/admin/diagnostic`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExamDiagnostic(data);
      } else {
        toast.error('Failed to fetch exam diagnostic');
      }
    } catch (e) {
      console.error('Error fetching exam diagnostic:', e);
      toast.error('Error fetching exam diagnostic');
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  // Open Exam Builder in EDIT mode — loads existing exam (admin route includes correct answers).
  // Resets stale state up-front so a fetch failure cannot open the modal with leftover data.
  const openExamForEdit = async (lesson, courseId) => {
    setLoadingExamForEdit(true);
    setEditingExam(null);
    setSelectedLessonForExam(null);
    setSelectedCourseIdForExam(null);
    setShowExamBuilder(false);
    try {
      const res = await fetch(`${API_URL}/api/exams/lesson/${lesson.lesson_id}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Failed to load exam (${res.status})`);
        return;
      }
      if (!data.exam) {
        toast.error('No exam found for this lesson');
        return;
      }
      setEditingExam(data.exam);
      setSelectedLessonForExam(lesson);
      setSelectedCourseIdForExam(courseId);
      setShowExamBuilder(true);
    } catch (e) {
      console.error('Failed to load exam for edit:', e);
      toast.error('Failed to load exam — check connection');
    } finally {
      setLoadingExamForEdit(false);
    }
  };

  // Add recording to a session
  const handleAddRecording = async () => {
    if (!newRecording.recording_url) {
      toast.error('Please enter a recording URL');
      return;
    }
    
    setAddingRecording(true);
    try {
      const params = new URLSearchParams({
        recording_url: newRecording.recording_url,
        title: newRecording.title || '',
        description: newRecording.description || '',
        duration_minutes: newRecording.duration_minutes || 60,
        is_public: newRecording.is_public
      });
      
      if (newRecording.session_id) {
        params.append('session_id', newRecording.session_id);
      }
      
      const res = await fetch(`${API_URL}/api/session-recordings/admin/add-recording?${params}`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        toast.success('Recording added successfully!');
        setShowAddRecordingModal(false);
        setNewRecording({
          session_id: '',
          recording_url: '',
          title: '',
          description: '',
          duration_minutes: 60,
          is_public: false
        });
        // Refresh the data
        fetchLiveSessions();
        fetchExistingRecordings();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add recording');
      }
    } catch (e) {
      toast.error('Error adding recording');
    } finally {
      setAddingRecording(false);
    }
  };

  // Confirm/Approve payment
  const handleConfirmPayment = async (sessionId, userEmail) => {
    const reason = prompt('Enter confirmation reason (e.g., "Bank transfer received", "Cash payment"):') || 'Payment confirmed by admin';
    if (!reason) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/override/process-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          session_id: sessionId, 
          action: 'approve',
          reason 
        })
      });
      if (res.ok) {
        toast.success(`Payment confirmed! Access granted to ${userEmail}`);
        fetchPayments();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to confirm payment');
      }
    } catch (e) {
      toast.error('Error confirming payment');
    }
  };

  // Reject payment
  const handleRejectPayment = async (sessionId, userEmail) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/override/process-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          session_id: sessionId, 
          action: 'reject',
          reason 
        })
      });
      if (res.ok) {
        toast.success(`Payment rejected for ${userEmail}`);
        fetchPayments();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to reject payment');
      }
    } catch (e) {
      toast.error('Error rejecting payment');
    }
  };

  // ============== APPOINTMENT HANDLERS ==============

  // Approve appointment
  const handleApproveAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/${appointmentId}/approve`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        toast.success('Appointment approved! Zoom link sent to mentee.');
        fetchAppointments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve appointment');
      }
    } catch (e) {
      toast.error('Error approving appointment');
    }
  };

  // Reject appointment
  const handleRejectAppointment = async (appointmentId) => {
    const reason = prompt('Enter rejection reason (optional):') || 'Slot not available';
    try {
      const res = await fetch(`${API_URL}/api/appointments/${appointmentId}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        toast.success('Appointment rejected');
        fetchAppointments();
      } else {
        toast.error('Failed to reject appointment');
      }
    } catch (e) {
      toast.error('Error rejecting appointment');
    }
  };

  // Open suggest alternatives modal
  const handleOpenSuggestModal = (appointment) => {
    setSelectedAppointment(appointment);
    setSuggestedDates([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setShowSuggestModal(true);
  };

  // Submit suggested alternatives
  const handleSubmitSuggestedDates = async () => {
    if (!selectedAppointment) {
      toast.error('No appointment selected');
      return;
    }
    
    const validDates = suggestedDates.filter(d => d.date && d.time);
    if (validDates.length === 0) {
      toast.error('Please provide at least one alternative date and time');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/appointments/${selectedAppointment.id}/suggest-alternatives`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          suggested_dates: validDates,
          message: `I've suggested some alternative times for our session. Please select one that works for you.`
        })
      });
      if (res.ok) {
        toast.success('Alternative dates sent to mentee');
        setShowSuggestModal(false);
        setSuggestedDates([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
        fetchAppointments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to suggest alternatives');
      }
    } catch (e) {
      console.error('Error suggesting alternatives:', e);
      toast.error('Error suggesting alternatives');
    }
  };

  // Book appointment for user (admin)
  const handleBookForUser = () => {
    navigate('/mentor/appointments');
  };

  // ============== POINTS MANAGEMENT ==============
  
  // Open points modal for a mentee
  const handleOpenPointsModal = async (mentee) => {
    setSelectedMenteeForPoints(mentee);
    setPointsToAward(10);
    setPointsReason('');
    setSelectedLevel(mentee.mentorship_level || 'disciple');
    setShowPointsModal(true);
  };

  // Award points to mentee
  const handleAwardPoints = async () => {
    if (!selectedMenteeForPoints || !pointsReason.trim()) {
      toast.error('Please provide a reason for the points');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/points/award`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: selectedMenteeForPoints.id,
          points: pointsToAward,
          reason: pointsReason
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Awarded ${pointsToAward} points! New total: ${data.new_total}`);
        setShowPointsModal(false);
        fetchMentees(); // Refresh mentee data
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to award points');
      }
    } catch (e) {
      toast.error('Error awarding points');
    }
  };

  // Set mentee level
  const handleSetLevel = async () => {
    if (!selectedMenteeForPoints) return;
    
    try {
      const res = await fetch(`${API_URL}/api/points/set-level`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: selectedMenteeForPoints.id,
          level: selectedLevel
        })
      });
      
      if (res.ok) {
        toast.success(`Level updated to ${selectedLevel}`);
        setShowPointsModal(false);
        fetchMentees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to set level');
      }
    } catch (e) {
      toast.error('Error setting level');
    }
  };

  // Send weekly reports to all enrolled users
  const handleSendWeeklyReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/weekly-reports/send-all`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Weekly reports queued for ${data.total_users} users`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send reports');
      }
    } catch (e) {
      toast.error('Error sending weekly reports');
    }
  };

  // ============== COMPUTED VALUES ==============

  const pendingUploads = uploads.filter(u => u.status === 'Pending Review' || u.status === 'pending');
  const pendingPayments = payments.filter(p => p.status === 'pending' || p.status === 'initiated');
  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const confirmedAppointments = appointments.filter(a => a.status === 'confirmed');
  const totalRevenue = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const filteredAppointments = appointments.filter(a => {
    if (appointmentFilter === 'pending') return a.status === 'pending';
    if (appointmentFilter === 'confirmed') return a.status === 'confirmed';
    if (appointmentFilter === 'cancelled') return a.status === 'cancelled';
    if (appointmentFilter === 'completed') return a.status === 'completed';
    return true;
  });
  
  // Computed values for enrolled vs non-enrolled
  const enrolledMentees = mentees.filter(m => 
    m.enrolled_mentorship_track || 
    m.membership_tier === 'mentorship' ||
    m.has_paid
  );
  const notEnrolledUsers = mentees.filter(m => 
    !m.enrolled_mentorship_track && 
    m.membership_tier !== 'mentorship' &&
    !m.has_paid
  );

  const filteredMentees = mentees.filter(m => {
    // Check if enrolled
    const isEnrolled = m.enrolled_mentorship_track || m.membership_tier === 'mentorship' || m.has_paid;
    
    if (menteeFilter === 'at-risk') return atRiskMentees.some(ar => ar.id === m.id);
    if (menteeFilter === 'active') return isEnrolled && m.status === 'active';
    if (menteeFilter === 'enrolled') return isEnrolled;
    if (menteeFilter === 'inactive') {
      if (!isEnrolled) return false; // Only enrolled can be inactive
      if (!m.last_active) return true;
      const daysSince = (Date.now() - new Date(m.last_active)) / (1000 * 60 * 60 * 24);
      return daysSince > 14;
    }
    if (menteeFilter === 'not-enrolled') return !isEnrolled;
    return true;
  }).filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUploads = uploads.filter(u => {
    if (uploadFilter === 'pending') return u.status === 'Pending Review' || u.status === 'pending';
    if (uploadFilter === 'approved') return u.status === 'Approved' || u.status === 'approved';
    if (uploadFilter === 'rejected') return u.status === 'Rejected' || u.status === 'rejected';
    return true;
  });

  // Sidebar navigation
  const navigation = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard, badge: null },
    { id: 'appointments', label: 'Appointments', icon: Calendar, badge: pendingAppointments.length || null },
    { id: 'inbox', label: 'Review Inbox', icon: FileText, badge: pendingUploads.length || null },
    { id: 'mentees', label: 'Mentees', icon: Users, badge: atRiskMentees.length || null },
    { id: 'assignments', label: 'Assignments', icon: Upload, badge: null },
    { id: 'announcements', label: 'Announcements', icon: Bell, badge: null },
    { id: 'recordings', label: 'Recordings', icon: Video, badge: null },
    { id: 'content', label: 'Content', icon: BookOpen, badge: null },
    { id: 'exams', label: 'Exam Diagnostic', icon: CheckCircle, badge: null },
    { id: 'payments', label: 'Payments', icon: CreditCard, badge: pendingPayments.length || null },
    { id: 'communications', label: 'Communications', icon: Mail, badge: null },
    { id: 'audit-log', label: 'Audit Log', icon: Shield, badge: null },
    { id: 'settings', label: 'Settings', icon: Settings, badge: null },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Control Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex pt-16">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside className={`
        w-64 bg-white border-r border-gray-200 fixed h-full z-40
        transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:pt-4
        ${sidebarOpen ? 'translate-x-0 pt-0' : '-translate-x-full'}
      `}>
        {/* Mobile header inside sidebar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-900">Menu</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="px-6 py-4 border-b">
          <h1 className="text-lg font-bold text-gray-900">Control Center</h1>
          <p className="text-sm text-gray-500">Admin & Mentor Dashboard</p>
        </div>
        
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id);
                setSidebarOpen(false); // Close sidebar on mobile after selection
              }}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                activeSection === item.id 
                  ? 'bg-purple-50 text-purple-700' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </div>
              {item.badge && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        {/* Quick Links */}
        <div className="absolute bottom-20 left-0 right-0 px-4">
          <div className="border-t pt-4 space-y-2">
            <button 
              onClick={() => navigate('/admin')}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" />
              Old Admin Panel
            </button>
            <button 
              onClick={() => navigate('/mentor')}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" />
              Old Mentor Portal
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="lg:ml-64 flex-1 p-4 lg:p-8 ml-0">
        {/* Mobile header with menu button */}
        <div className="lg:hidden flex items-center gap-3 mb-4 -mt-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 bg-white rounded-lg shadow-sm border hover:bg-gray-50"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
          <div>
            <h2 className="font-semibold text-gray-900 capitalize">{activeSection === 'overview' ? 'Dashboard' : activeSection.replace('-', ' ')}</h2>
          </div>
        </div>
        
        {/* ============== OVERVIEW SECTION ============== */}
        {activeSection === 'overview' && (
          <OverviewSection
            user={user}
            mentees={mentees}
            courses={courses}
            ebooks={ebooks}
            payments={payments}
            uploads={uploads}
            pendingUploads={pendingUploads}
            pendingPayments={pendingPayments}
            pendingAppointments={pendingAppointments}
            atRiskMentees={atRiskMentees}
            analytics={analytics}
            totalRevenue={totalRevenue}
            assignmentSubmissions={assignmentSubmissions}
            syncingLibrary={syncingLibrary}
            setActiveSection={setActiveSection}
            setMenteeFilter={setMenteeFilter}
            fetchAllData={fetchAllData}
            handleSyncLibrary={handleSyncLibrary}
            categorizeLoading={categorizeLoading}
            categorizeResult={categorizeResult}
            handleCategorizeLibrary={handleCategorizeLibrary}
            handleDownloadFile={handleDownloadFile}
            handleApproveUpload={handleApproveUpload}
            handleRejectUpload={handleRejectUpload}
            fetchAssignmentSubmissions={fetchAssignmentSubmissions}
            setSelectedSubmission={setSelectedSubmission}
            setShowReviewModal={setShowReviewModal}
            handleMessageMentee={handleMessageMentee}
            handleViewMenteeProfile={handleViewMenteeProfile}
          />
        )}


        {/* ============== APPOINTMENTS SECTION ============== */}
        {activeSection === 'appointments' && (
          <AppointmentsSection
            appointments={appointments}
            pendingAppointments={pendingAppointments}
            confirmedAppointments={confirmedAppointments}
            filteredAppointments={filteredAppointments}
            appointmentFilter={appointmentFilter}
            setAppointmentFilter={setAppointmentFilter}
            handleApproveAppointment={handleApproveAppointment}
            handleRejectAppointment={handleRejectAppointment}
            handleOpenSuggestModal={handleOpenSuggestModal}
            handleBookForUser={handleBookForUser}
            showSuggestModal={showSuggestModal}
            setShowSuggestModal={setShowSuggestModal}
            selectedAppointment={selectedAppointment}
            suggestedDates={suggestedDates}
            setSuggestedDates={setSuggestedDates}
            handleSubmitSuggestedDates={handleSubmitSuggestedDates}
          />
        )}



        {/* ============== INBOX SECTION ============== */}
        {activeSection === 'inbox' && (
          <ReviewInboxSection
            filteredUploads={filteredUploads}
            uploadFilter={uploadFilter}
            setUploadFilter={setUploadFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedUploads={selectedUploads}
            setSelectedUploads={setSelectedUploads}
            handleBulkApprove={handleBulkApprove}
            handleDownloadFile={handleDownloadFile}
            handleApproveUpload={handleApproveUpload}
            handleRejectUpload={handleRejectUpload}
          />
        )}


        {/* ============== MENTEES SECTION ============== */}
        {activeSection === 'mentees' && (
          <MenteesSection
            mentees={mentees}
            filteredMentees={filteredMentees}
            enrolledMentees={enrolledMentees}
            notEnrolledUsers={notEnrolledUsers}
            atRiskMentees={atRiskMentees}
            menteeFilter={menteeFilter}
            setMenteeFilter={setMenteeFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleViewMenteeProfile={handleViewMenteeProfile}
            handleMessageMentee={handleMessageMentee}
            handleOpenPointsModal={handleOpenPointsModal}
          />
        )}



        {/* ============== CONTENT SECTION ============== */}
        {activeSection === 'content' && (
          <ContentSection
            courses={courses}
            ebooks={ebooks}
            handleEditCourse={handleEditCourse}
          />
        )}


        {/* ============== ASSIGNMENTS SECTION ============== */}
        {activeSection === 'assignments' && (
          <AssignmentsSection
            assignments={assignments}
            setShowAssignmentModal={setShowAssignmentModal}
            handleDeleteAssignment={handleDeleteAssignment}
            handleTakedownAssignment={handleTakedownAssignment}
            handleRestoreAssignment={handleRestoreAssignment}
          />
        )}


        {activeSection === 'exams' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Exam Diagnostic</h1>
                <p className="text-gray-500">Check which lessons have exams and identify missing exams</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* Course-specific exam import - currently only Spiritual Growth has predefined questions */}
                <select
                  className="border rounded-lg px-3 py-2"
                  onChange={(e) => {
                    const courseTitle = e.target.value;
                    if (!courseTitle) return;
                    
                    // Find the course info
                    const course = examDiagnostic?.courses?.find(c => c.course_title === courseTitle);
                    const hasPresetQuestions = courseTitle === 'Spiritual Growth' || courseTitle === 'Leadership Development';
                    setSelectedCourseForImport({
                      title: courseTitle,
                      id: course?.course_id,
                      lessons: course?.lessons || [],
                      hasPresetQuestions: hasPresetQuestions
                    });
                    setShowExamImportModal(true);
                    e.target.value = '';
                  }}
                  defaultValue=""
                >
                  <option value="">Import Exam Questions...</option>
                  <option value="Spiritual Growth">Spiritual Growth (6 exams ready)</option>
                  <option value="Leadership Development">Leadership Development (4 exams ready)</option>
                  {examDiagnostic?.courses?.filter(c => c.course_title !== 'Spiritual Growth' && c.course_title !== 'Leadership Development').map(course => (
                    <option key={course.course_id} value={course.course_title}>
                      {course.course_title}
                    </option>
                  ))}
                </select>
                <button 
                  onClick={fetchExamDiagnostic}
                  disabled={loadingDiagnostic}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:bg-purple-300"
                  data-testid="run-diagnostic-btn"
                >
                  {loadingDiagnostic ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Run Diagnostic
                    </>
                  )}
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Seed 12-question MCQ exams for ALL 20 supported lessons?\n\nThis OVERWRITES any existing exams with high-quality content-based questions, passing score 100%.\n\nInstant. No videos. No LLM cost.')) return;
                    try {
                      const res = await fetch(`${API_URL}/api/admin/seed-hardcoded-exams`, {
                        method: 'POST',
                        headers: { ...headers, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ overwrite: true, passing_score: 100 }),
                      });
                      const data = await res.json();
                      if (!res.ok) { toast.error(data.detail || 'Seeding failed'); return; }
                      const s = data.summary;
                      toast.success(`Seeded ${s.created + s.updated} exams (${s.created} new, ${s.updated} updated, ${s.missing} lessons missing)`);
                      fetchExamDiagnostic();
                    } catch (e) {
                      console.error(e);
                      toast.error('Seeding failed — check console');
                    }
                  }}
                  data-testid="seed-hardcoded-btn"
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 flex items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Seed 240 Hardcoded MCQs
                </button>
              </div>
            </div>
            
            {/* Summary */}
            {examDiagnostic && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-sm text-gray-500">Total Courses</p>
                  <p className="text-2xl font-bold text-gray-900">{examDiagnostic.summary.total_courses}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-sm text-gray-500">Total Lessons</p>
                  <p className="text-2xl font-bold text-gray-900">{examDiagnostic.summary.total_lessons}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-sm text-gray-500">With Exams</p>
                  <p className="text-2xl font-bold text-green-600">{examDiagnostic.summary.lessons_with_exams}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-sm text-gray-500">Missing Exams</p>
                  <p className="text-2xl font-bold text-red-600">{examDiagnostic.summary.lessons_without_exams}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-sm text-gray-500">Orphaned Exams</p>
                  <p className="text-2xl font-bold text-orange-600">{examDiagnostic.summary.orphaned_exams || 0}</p>
                </div>
              </div>
            )}
            
            {/* Orphaned Exams Alert */}
            {examDiagnostic && examDiagnostic.orphaned_exams && examDiagnostic.orphaned_exams.length > 0 && (
              <div data-section="orphaned-exams" className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Found {examDiagnostic.orphaned_exams.length} Orphaned Exams
                </h3>
                <p className="text-orange-700 text-sm mb-4">
                  These exams exist in your database but are linked to old/deleted lesson IDs. 
                  You can re-link them to the correct lessons below.
                </p>
                <div className="space-y-3">
                  {examDiagnostic.orphaned_exams.map((orphan) => (
                    <div key={orphan.exam_id} className="bg-white p-4 rounded-lg border border-orange-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{orphan.exam_title}</p>
                          <p className="text-xs text-gray-500 font-mono">Exam ID: {orphan.exam_id}</p>
                          <p className="text-xs text-orange-600 font-mono">Linked to invalid lesson: {orphan.linked_lesson_id}</p>
                          <p className="text-sm text-gray-600 mt-1">{orphan.questions_count} questions</p>
                        </div>
                        <select
                          className="px-3 py-2 border rounded-lg text-sm"
                          onChange={async (e) => {
                            if (!e.target.value) return;
                            if (!window.confirm(`Re-link "${orphan.exam_title}" to this lesson?`)) {
                              e.target.value = '';
                              return;
                            }
                            try {
                              const res = await fetch(`${API_URL}/api/exams/admin/relink-exam?exam_id=${orphan.exam_id}&new_lesson_id=${e.target.value}`, {
                                method: 'POST',
                                headers
                              });
                              if (res.ok) {
                                toast.success('Exam re-linked successfully!');
                                fetchExamDiagnostic();
                              } else {
                                const data = await res.json();
                                toast.error(data.detail || 'Failed to re-link exam');
                              }
                            } catch (err) {
                              toast.error('Failed to re-link exam');
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">Select lesson to link...</option>
                          {examDiagnostic.courses.flatMap(c => 
                            c.lessons.filter(l => !l.has_exam).map(l => (
                              <option key={l.lesson_id} value={l.lesson_id}>
                                {c.course_title} - {l.lesson_title}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Diagnostic Results */}
            {examDiagnostic ? (
              <div className="space-y-4">
                {examDiagnostic.courses.map((course) => (
                  <div key={course.course_id} className="bg-white rounded-xl border overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-semibold text-gray-900">{course.course_title}</h3>
                        <p className="text-sm text-gray-500">
                          {course.lessons.filter(l => l.has_exam).length} of {course.lessons.length} lessons have exams
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          if (!window.confirm(`Auto-generate exams for ALL ${course.lessons.length} lessons in "${course.course_title}"?\n\nThis will:\n• Download each lesson's video from Google Drive\n• Transcribe the audio with Whisper\n• Generate 12 MCQ questions per lesson with Claude Sonnet 4.5\n• OVERWRITE any existing exams\n\nEstimated time: ~1-2 min per lesson. You can leave this page; the job runs in the background.`)) return;
                          startAutoGenerate({ courseId: course.course_id });
                        }}
                        disabled={Boolean(activeJobId)}
                        data-testid={`auto-gen-course-${course.course_id}`}
                        className="px-3 py-2 bg-gradient-to-r from-purple-600 to-orange-500 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50"
                      >
                        <Sparkles className="w-4 h-4" />
                        Auto-Generate All Exams (from video)
                      </button>
                    </div>
                    <div className="divide-y">
                      {course.lessons.map((lesson) => (
                        <div 
                          key={lesson.lesson_id} 
                          className={`p-4 flex items-center justify-between ${!lesson.has_exam ? 'bg-red-50' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              lesson.has_exam ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {lesson.lesson_order || '?'}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900">{lesson.lesson_title}</p>
                              <p className="text-xs text-gray-500 font-mono">ID: {lesson.lesson_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {generatingLessonId === lesson.lesson_id && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full animate-pulse">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Generating…
                              </span>
                            )}
                            {lesson.has_exam ? (
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                    <CheckCircle className="w-3 h-3" />
                                    Has Exam ({lesson.question_count || 0} Q)
                                  </span>
                                  <p className="text-xs text-gray-500 mt-1">{lesson.exam_title}</p>
                                </div>
                                <button
                                  onClick={() => openExamForEdit(lesson, course.course_id)}
                                  disabled={loadingExamForEdit}
                                  data-testid={`edit-exam-btn-${lesson.lesson_id}`}
                                  className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-1 disabled:opacity-50"
                                >
                                  <PenTool className="w-3 h-3" />
                                  {loadingExamForEdit ? 'Loading…' : 'Edit'}
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Regenerate exam from "${lesson.lesson_title}" video?\n\nThis will OVERWRITE the existing exam with 12 fresh MCQs from Claude Sonnet 4.5.`)) {
                                      startAutoGenerate({ lessonId: lesson.lesson_id });
                                    }
                                  }}
                                  disabled={Boolean(activeJobId)}
                                  data-testid={`auto-gen-lesson-${lesson.lesson_id}`}
                                  title="Auto-generate from video"
                                  className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  Regenerate
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                  <X className="w-3 h-3" />
                                  NO EXAM
                                </span>
                                <button
                                  onClick={() => {
                                    setEditingExam(null);
                                    setSelectedLessonForExam(lesson);
                                    setSelectedCourseIdForExam(course.course_id);
                                    setShowExamBuilder(true);
                                  }}
                                  data-testid={`create-exam-btn-${lesson.lesson_id}`}
                                  className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                                >
                                  <PenTool className="w-3 h-3" />
                                  Manual
                                </button>
                                <button
                                  onClick={() => startAutoGenerate({ lessonId: lesson.lesson_id })}
                                  disabled={Boolean(activeJobId)}
                                  data-testid={`auto-gen-lesson-${lesson.lesson_id}`}
                                  title="Auto-generate from video"
                                  className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-orange-500 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-1 disabled:opacity-50"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  Auto-Generate
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-8 text-center">
                <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Click "Run Diagnostic" to check exam status for all lessons</p>
              </div>
            )}
          </div>
        )}

        {/* Exam Import Modal */}
        {showExamImportModal && selectedCourseForImport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    Import Exams for: {selectedCourseForImport.title}
                  </h2>
                  <button 
                    onClick={() => {
                      setShowExamImportModal(false);
                      setSelectedCourseForImport(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {selectedCourseForImport.hasPresetQuestions ? (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-green-800">Pre-configured Questions Available</h3>
                          <p className="text-green-700 text-sm mt-1">
                            {selectedCourseForImport.title === 'Spiritual Growth' 
                              ? 'This course has 6 complete exams with 13-15 questions each, ready to import.'
                              : selectedCourseForImport.title === 'Leadership Development'
                              ? 'This course has 4 complete exams with 8 questions each, ready to import.'
                              : 'This course has exams ready to import.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="font-semibold mb-3">What will be imported:</h3>
                      {selectedCourseForImport.title === 'Spiritual Growth' && (
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 1: Introduction to Spiritual Growth - 13 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 2: Building Spiritual Foundation - 15 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 3: Developing Spiritual Sensitivity - 14 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 4: Walking in the Spirit - 15 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 5: Spiritual Warfare Basics - 14 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 6: Stewardship, Obedience, Fellowship Finale - 14 questions
                          </li>
                        </ul>
                      )}
                      {selectedCourseForImport.title === 'Leadership Development' && (
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 1: The Hidden Leader - 8 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 2: Overcoming Challenges - 8 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 3: Journey to Self Discovery - 8 questions
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Lesson 4: Self-Discovery in Leadership - 8 questions
                          </li>
                        </ul>
                      )}
                    </div>
                    
                    <button
                      onClick={async () => {
                        setImportingExams(true);
                        try {
                          const endpoint = selectedCourseForImport.title === 'Spiritual Growth'
                            ? `${API_URL}/api/exams/admin/import-spiritual-growth-exams`
                            : selectedCourseForImport.title === 'Leadership Development'
                            ? `${API_URL}/api/exams/admin/import-leadership-exams`
                            : null;
                          
                          if (!endpoint) {
                            toast.error('No import endpoint for this course');
                            return;
                          }
                          
                          const res = await fetch(endpoint, {
                            method: 'POST',
                            headers
                          });
                          const data = await res.json();
                          if (res.ok) {
                            toast.success(`${data.message}. ${data.results?.length || 0} exams processed.`);
                            fetchExamDiagnostic();
                            setShowExamImportModal(false);
                            setSelectedCourseForImport(null);
                          } else {
                            toast.error(data.detail || 'Failed to import exams');
                          }
                        } catch (err) {
                          toast.error('Error importing exams');
                        } finally {
                          setImportingExams(false);
                        }
                      }}
                      disabled={importingExams}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium disabled:bg-green-300 flex items-center justify-center gap-2"
                    >
                      {importingExams ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5" />
                          Import All {selectedCourseForImport.title === 'Spiritual Growth' ? '6' : '4'} Exams Now
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-orange-800">No Pre-configured Questions</h3>
                          <p className="text-orange-700 text-sm mt-1">
                            This course doesn't have pre-configured exam questions yet. You can:
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="font-semibold">Options for this course:</h3>
                      
                      {/* Option 1: Re-link orphaned exams */}
                      {examDiagnostic?.orphaned_exams?.length > 0 && (
                        <div className="border rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">Option 1: Re-link Orphaned Exams</h4>
                          <p className="text-sm text-gray-600 mb-3">
                            You have {examDiagnostic.orphaned_exams.length} orphaned exams in your database. 
                            Use the "Found Orphaned Exams" section below to link them to lessons in this course.
                          </p>
                          <button
                            onClick={() => {
                              setShowExamImportModal(false);
                              setSelectedCourseForImport(null);
                              // Scroll to orphaned exams section
                              const orphanedSection = document.querySelector('[data-section="orphaned-exams"]');
                              if (orphanedSection) orphanedSection.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                          >
                            Go to Orphaned Exams →
                          </button>
                        </div>
                      )}
                      
                      {/* Option 2: Create exams manually */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Option 2: Create Exams in Content Manager</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Go to Content → Manage Courses → Select a lesson → Add Exam to create exams manually.
                        </p>
                        <button
                          onClick={() => {
                            setShowExamImportModal(false);
                            setSelectedCourseForImport(null);
                            navigate('/admin/content');
                          }}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          Go to Content Manager →
                        </button>
                      </div>
                      
                      {/* Option 3: Request bulk import */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="font-medium text-gray-900 mb-2">Option 3: Bulk Import from File</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          If you have exam questions in a document, share them with support and we can configure bulk import for this course.
                        </p>
                        <p className="text-xs text-gray-500">
                          Format: Lesson name, Question, Options A/B/C/D, Correct Answer
                        </p>
                      </div>
                    </div>
                    
                    {/* Show lessons in this course that need exams */}
                    {selectedCourseForImport.lessons?.length > 0 && (
                      <div className="border rounded-lg">
                        <div className="p-3 bg-gray-50 border-b">
                          <h4 className="font-medium">Lessons in this course:</h4>
                        </div>
                        <div className="divide-y max-h-60 overflow-y-auto">
                          {selectedCourseForImport.lessons.map((lesson, idx) => (
                            <div key={lesson.lesson_id} className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs">
                                  {idx + 1}
                                </span>
                                <span className="text-sm">{lesson.lesson_title}</span>
                              </div>
                              {lesson.has_exam ? (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Has Exam
                                </span>
                              ) : (
                                <span className="text-xs text-red-600 flex items-center gap-1">
                                  <X className="w-3 h-3" />
                                  No Exam
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="p-4 border-t bg-gray-50 flex justify-end">
                <button
                  onClick={() => {
                    setShowExamImportModal(false);
                    setSelectedCourseForImport(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exam Builder Modal */}
        {showExamBuilder && selectedLessonForExam && (
          <ExamBuilder
            lesson={selectedLessonForExam}
            courseId={selectedCourseIdForExam}
            existingExam={editingExam}
            onClose={() => {
              setShowExamBuilder(false);
              setSelectedLessonForExam(null);
              setSelectedCourseIdForExam(null);
              setEditingExam(null);
            }}
            onSaved={() => {
              fetchExamDiagnostic();
            }}
          />
        )}

        {/* Auto-Generate Progress Modal */}
        {activeJobId && activeJob && (
          <div className="fixed bottom-6 right-6 z-50 w-96 bg-white shadow-2xl border border-purple-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-orange-500 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                {activeJob.status === 'running'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Sparkles className="w-4 h-4" />}
                <span className="text-sm font-semibold">
                  {activeJob.status === 'running' ? 'Generating exams…' : 'Generation complete'}
                </span>
              </div>
              {activeJob.status === 'running' ? (
                <button onClick={cancelActiveJob} className="text-xs underline hover:opacity-80" data-testid="cancel-job-btn">Cancel</button>
              ) : (
                <button onClick={() => { setActiveJobId(null); setActiveJob(null); }} className="hover:opacity-80" data-testid="close-job-modal">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Progress</span>
                <span className="font-mono">{activeJob.processed}/{activeJob.total}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-600 to-orange-500 transition-all duration-300"
                  style={{ width: `${activeJob.total ? (activeJob.processed / activeJob.total) * 100 : 0}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-green-50 rounded">
                  <div className="font-semibold text-green-700">{activeJob.succeeded}</div>
                  <div className="text-gray-500">Generated</div>
                </div>
                <div className="p-2 bg-red-50 rounded">
                  <div className="font-semibold text-red-700">{activeJob.failed}</div>
                  <div className="text-gray-500">Failed</div>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-700">{Math.max((activeJob.total || 0) - (activeJob.processed || 0), 0)}</div>
                  <div className="text-gray-500">Pending</div>
                </div>
              </div>
              {activeJob.current_message && activeJob.status === 'running' && (
                <div className="p-2 bg-purple-50 border border-purple-100 rounded text-xs text-purple-900">
                  <div className="font-semibold mb-1">{activeJob.current_step?.replace(/_/g, ' ') || 'Working'}</div>
                  <div className="text-purple-700 truncate" title={activeJob.current_message}>{activeJob.current_message}</div>
                </div>
              )}
              {activeJob.last_lesson_title && activeJob.status === 'running' && (
                <p className="text-xs text-gray-500 italic truncate" title={activeJob.last_lesson_title}>
                  Last: {activeJob.last_lesson_title}
                </p>
              )}
              {activeJob.errors && activeJob.errors.length > 0 && (
                <details className="text-xs">
                  <summary className="text-red-600 cursor-pointer">{activeJob.errors.length} error(s)</summary>
                  <div className="mt-1 max-h-32 overflow-y-auto space-y-1">
                    {activeJob.errors.slice(-5).map((e, i) => (
                      <div key={i} className="p-1 bg-red-50 text-red-700 rounded">
                        {(e.error || '').slice(0, 200)}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>
        )}

        {/* ============== ANNOUNCEMENTS SECTION ============== */}
        {activeSection === 'announcements' && (
          <AnnouncementsSection
            announcements={announcements}
            setShowAnnouncementModal={setShowAnnouncementModal}
            handleDeleteAnnouncement={handleDeleteAnnouncement}
          />
        )}


        {/* ============== PAYMENTS SECTION ============== */}
        {activeSection === 'payments' && (
          <PaymentsSection
            payments={payments}
            pendingPayments={pendingPayments}
            totalRevenue={totalRevenue}
            handleConfirmPayment={handleConfirmPayment}
            handleRejectPayment={handleRejectPayment}
          />
        )}



        {/* ============== RECORDINGS SECTION ============== */}
        {activeSection === 'recordings' && (
          <RecordingsSection
            liveSessions={liveSessions}
            existingRecordings={existingRecordings}
            newRecording={newRecording}
            setNewRecording={setNewRecording}
            showAddRecordingModal={showAddRecordingModal}
            setShowAddRecordingModal={setShowAddRecordingModal}
            fetchLiveSessions={fetchLiveSessions}
            fetchExistingRecordings={fetchExistingRecordings}
          />
        )}


        {/* ============== COMMUNICATIONS SECTION ============== */}
        {activeSection === 'communications' && (
          <CommunicationsSection
            setShowBulkEmailModal={setShowBulkEmailModal}
            handleSendWeeklyReports={handleSendWeeklyReports}
          />
        )}

        {/* ============== AUDIT LOG SECTION ============== */}
        {activeSection === 'audit-log' && (
          <AdminAuditLog />
        )}


        {/* ============== SETTINGS SECTION ============== */}
        {activeSection === 'settings' && (
          <SettingsSection />
        )}


      </main>

      {/* ============== POINTS MANAGEMENT MODAL ============== */}
      {showPointsModal && selectedMenteeForPoints && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold">Manage Points & Level</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {selectedMenteeForPoints.name || selectedMenteeForPoints.email}
                </p>
              </div>
              <button 
                onClick={() => setShowPointsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Current Stats */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Current Points</p>
                  <p className="text-2xl font-bold text-purple-600">{selectedMenteeForPoints.points || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Level</p>
                  <p className="text-lg font-semibold">
                    {selectedMenteeForPoints.mentorship_level === 'burningOne' ? 'Burning One' :
                     selectedMenteeForPoints.mentorship_level?.charAt(0).toUpperCase() + selectedMenteeForPoints.mentorship_level?.slice(1) || 'Disciple'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Award Points Section */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Award Points</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Points to Award</label>
                  <div className="flex gap-2">
                    {[10, 25, 50, 100].map((pts) => (
                      <button
                        key={pts}
                        onClick={() => setPointsToAward(pts)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pointsToAward === pts 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        +{pts}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    value={pointsToAward}
                    onChange={(e) => setPointsToAward(parseInt(e.target.value) || 0)}
                    className="w-full mt-2 px-3 py-2 border rounded-lg"
                    min="1"
                    max="500"
                    placeholder="Custom amount"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Reason</label>
                  <input
                    type="text"
                    value={pointsReason}
                    onChange={(e) => setPointsReason(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Excellent homework submission"
                  />
                </div>
                <button
                  onClick={handleAwardPoints}
                  className="w-full py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  Award {pointsToAward} Points
                </button>
              </div>
            </div>
            
            {/* Set Level Section */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-3">Override Level</h3>
              <div className="space-y-3">
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="disciple">Disciple (0+ pts)</option>
                  <option value="builder">Builder (100+ pts)</option>
                  <option value="burningOne">Burning One (300+ pts)</option>
                  <option value="ambassador">Ambassador (600+ pts)</option>
                </select>
                <button
                  onClick={handleSetLevel}
                  className="w-full py-2 border border-purple-200 text-purple-600 rounded-lg font-medium hover:bg-purple-50"
                >
                  Set Level to {selectedLevel === 'burningOne' ? 'Burning One' : selectedLevel?.charAt(0).toUpperCase() + selectedLevel?.slice(1)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Creation Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Create Assignment</h2>
              <button onClick={() => setShowAssignmentModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newAssignment.title}
                  onChange={(e) => setNewAssignment({...newAssignment, title: e.target.value})}
                  placeholder="e.g., Weekly Reflection Paper"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newAssignment.description}
                  onChange={(e) => setNewAssignment({...newAssignment, description: e.target.value})}
                  placeholder="Describe the assignment requirements..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newAssignment.category}
                    onChange={(e) => setNewAssignment({...newAssignment, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="homework">Homework</option>
                    <option value="project">Project</option>
                    <option value="reflection">Reflection</option>
                    <option value="reading">Reading</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deadline *</label>
                  <input
                    type="date"
                    value={newAssignment.deadline}
                    onChange={(e) => setNewAssignment({...newAssignment, deadline: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              {/* File Attachment Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  <Upload className="w-4 h-4 inline mr-1" />
                  Attach File (Optional)
                </label>
                <p className="text-xs text-blue-600 mb-2">
                  Upload PDFs, documents, or other materials for this assignment.
                </p>
                <input
                  type="file"
                  ref={assignmentFileInputRef}
                  onChange={(e) => setAssignmentFile(e.target.files[0])}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => assignmentFileInputRef.current?.click()}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    Choose File
                  </button>
                  {assignmentFile && (
                    <div className="flex items-center gap-2 text-sm text-blue-800 bg-blue-100 px-3 py-1 rounded-full">
                      <FileText className="w-4 h-4" />
                      <span className="truncate max-w-[150px]">{assignmentFile.name}</span>
                      <button 
                        onClick={() => {
                          setAssignmentFile(null);
                          if (assignmentFileInputRef.current) {
                            assignmentFileInputRef.current.value = '';
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setAssignmentFile(null);
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAssignment}
                disabled={creatingAssignment}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creatingAssignment ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Assignment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Creation Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">New Announcement</h2>
              <button onClick={() => setShowAnnouncementModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                  placeholder="e.g., Important Update"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={newAnnouncement.message}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
                  placeholder="Write your announcement message..."
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="target_type"
                      value="all"
                      checked={newAnnouncement.target_type === 'all'}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, target_type: e.target.value})}
                      className="text-purple-600"
                    />
                    <span>All Mentees</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="target_type"
                      value="selected"
                      checked={newAnnouncement.target_type === 'selected'}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, target_type: e.target.value})}
                      className="text-purple-600"
                    />
                    <span>Selected Users</span>
                  </label>
                </div>
              </div>
              
              {newAnnouncement.target_type === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Mentees</label>
                  <select
                    multiple
                    value={newAnnouncement.target_user_ids}
                    onChange={(e) => setNewAnnouncement({
                      ...newAnnouncement, 
                      target_user_ids: Array.from(e.target.selectedOptions, option => option.value)
                    })}
                    className="w-full px-3 py-2 border rounded-lg h-32"
                  >
                    {mentees.filter(m => m.membership_tier === 'mentorship').map(m => (
                      <option key={m.id} value={m.id}>{m.name || m.email}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
                <input
                  type="date"
                  value={newAnnouncement.expires_at}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, expires_at: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAnnouncementModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAnnouncement}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Send Announcement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============== BULK EMAIL MODAL ============== */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold">Send Bulk Email</h2>
                <p className="text-gray-500 text-sm">Email will be sent to all mentees</p>
              </div>
              <button onClick={() => setShowBulkEmailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={bulkEmail.subject}
                  onChange={(e) => setBulkEmail({...bulkEmail, subject: e.target.value})}
                  placeholder="e.g., Important Update from Obed Ansu Academy"
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="bulk-email-subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={bulkEmail.message}
                  onChange={(e) => setBulkEmail({...bulkEmail, message: e.target.value})}
                  placeholder="Write your message here... HTML formatting is supported."
                  rows="8"
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="bulk-email-message"
                />
                <p className="text-xs text-gray-500 mt-1">The message will be wrapped in a beautiful email template</p>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Recipients:</strong> All mentees (excluding admins and mentors)
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowBulkEmailModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleSendBulkEmail}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-bulk-email-submit"
              >
                {sendingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Emails
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============== ADD RECORDING MODAL ============== */}
      {showAddRecordingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold">Add Session Recording</h2>
                <p className="text-gray-500 text-sm">Upload a Zoom recording for mentees to watch</p>
              </div>
              <button onClick={() => setShowAddRecordingModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Session Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Session (Optional)</label>
                <select
                  value={newRecording.session_id}
                  onChange={(e) => {
                    const session = liveSessions.find(s => s.id === e.target.value);
                    setNewRecording({
                      ...newRecording,
                      session_id: e.target.value,
                      title: session?.title || newRecording.title
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">-- Standalone Recording --</option>
                  {liveSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.title} ({session.scheduled_date ? new Date(session.scheduled_date).toLocaleDateString() : 'No date'})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Recording URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recording URL *</label>
                <input
                  type="url"
                  value={newRecording.recording_url}
                  onChange={(e) => setNewRecording({...newRecording, recording_url: e.target.value})}
                  placeholder="https://zoom.us/rec/share/..."
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="recording-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">Paste the Zoom sharing URL or YouTube/Vimeo link</p>
              </div>
              
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newRecording.title}
                  onChange={(e) => setNewRecording({...newRecording, title: e.target.value})}
                  placeholder="e.g., Week 3: Spiritual Growth Session"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newRecording.description}
                  onChange={(e) => setNewRecording({...newRecording, description: e.target.value})}
                  placeholder="Brief description of what was covered..."
                  rows="3"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={newRecording.duration_minutes}
                  onChange={(e) => setNewRecording({...newRecording, duration_minutes: parseInt(e.target.value) || 60})}
                  min="1"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              {/* Public Toggle */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={newRecording.is_public}
                  onChange={(e) => setNewRecording({...newRecording, is_public: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="is_public" className="text-sm">
                  <span className="font-medium">Make Public</span>
                  <span className="text-gray-500 block">Allow non-enrolled users to view this recording</span>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddRecordingModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={addingRecording}
              >
                Cancel
              </button>
              <button
                onClick={handleAddRecording}
                disabled={addingRecording || !newRecording.recording_url}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-recording-btn"
              >
                {addingRecording ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    Add Recording
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Review Modal */}
      {showReviewModal && selectedSubmission && (
        <AssignmentReviewModal
          submission={selectedSubmission}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedSubmission(null);
          }}
          onReviewed={() => {
            fetchAssignmentSubmissions();
          }}
        />
      )}
    </div>
  );
};

export default UnifiedControlCenter;
